// Components
import './CookieBanner.scss';
import Cookie, { type CookieAttributes } from 'js-cookie';
import { useEffect, useRef } from 'react';

import { Button } from '@/components/Button/Button';
import { languageStrings } from '@/l18n/de';
// Modules
import { config } from '@/lib/config';
import { sendBundleReference } from '@/lib/tracking';
import { getOffsetDate, getUTMParamsFromSessionStorage } from '@/lib/util';

const CookieBanner = () => {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const utm = getUTMParamsFromSessionStorage();

	useEffect(() => {
		if (!dialogRef.current) {
			return;
		}

		const cookieAccepted = Cookie.get(config.cookie.consent) === 'true';
		const isPrivacyWallCampaign = utm?.utmCampaign === 'privacywall';

		if (!cookieAccepted && !isPrivacyWallCampaign && !dialogRef.current.open) {
			dialogRef.current.showModal();
		}
	}, [utm?.utmCampaign]);

	const handleClick = () => {
		/**
		 * We used to set the expiry year to five years in the future. However,
		 * browsers only allow a maximum age of 400 days for cookies.
		 * {@see {@link https://developer.chrome.com/blog/cookie-max-age-expires}}
		 */
		const options: CookieAttributes = {
			domain: config.cookie.domain,
			expires: getOffsetDate(1),
			sameSite: 'strict'
		};

		Cookie.set(config.cookie.consent, 'true', options);

		dialogRef.current?.close();

		sendBundleReference();
	};

	return (
		<dialog
			className="dialog"
			data-nosnippet // This prevents the cookie banner from being displayed in (Google) search results - https://developers.google.com/search/docs/crawling-indexing/robots-meta-tag#data-nosnippet-attr
			id="cookie-bar"
			ref={dialogRef}
		>
			<div className="dialog--surface">
				<p className="dialog--description">{languageStrings.dialogs.cookie.description}</p>

				<div className="dialog--content">
					<Button
						ariaLabel="Datenschutzerklärung akzeptieren"
						className="form--button-weak form--button-small"
						onClick={handleClick}
						type="button"
					>
						{languageStrings.dialogs.cookie.action}
					</Button>

					<a
						href="https://apps.derstandard.at/Datenschutz"
						rel="noopener noreferrer"
						tabIndex={0}
						target="_blank"
					>
						{languageStrings.dialogs.cookie.goToPrivacyPolicy}
					</a>
				</div>
			</div>
		</dialog>
	);
};

export { CookieBanner };
